import clsx from "clsx";
import styles from "./styles.module.scss";
import type { DetailData as DetailDataType } from "../../../../../contexts/chat-log/chat-log-context";
import { useChatbotConfig } from "../../../../../contexts/chatbot-config";
import Image from "./image";
import ImageDataset from "./image-dataset";
import Youtube from "./youtube";
import YoutubeShorts from "./youtube-shorts";
import Video from "./video";
import LinkCard from "./link-card";
import File from "./file";
import Cta from "./cta";
import PDFViewer from "../../../pdf-viewer";

export default function DetailData({
  detailData,
  className,
}: {
  detailData: DetailDataType[];
  className?: string;
}) {
  const { chatbotData } = useChatbotConfig();
  const baseUrl = chatbotData?.chatbot_S3_URL || "";

  // 実際に表示されるカード枚数を事前カウントし、合計1枚のときだけ全幅表示にする
  const totalCardCount = detailData.reduce((sum, data) => {
    const type = (data.file_type || "").toUpperCase();
    if (type === "DATASET" || type === "IMAGE_SET" || type === "IMAGESET" || type === "CTA") {
      const count = data.file_path.split(",").map((p) => p.trim()).filter(Boolean).length;
      return sum + count;
    }
    return sum + 1;
  }, 0);
  const isSolo = totalCardCount === 1;

  return (
    <>
      {detailData.length > 0 && (
        <div
          className={clsx(styles.wrapper, className)}
          onMouseDown={(e) => {
            const el = e.currentTarget;
            el.style.cursor = "grabbing";
            el.style.userSelect = "none";
            const startX = e.pageX - el.offsetLeft;
            const scrollLeft = el.scrollLeft;
            const onMove = (ev: MouseEvent) => {
              const x = ev.pageX - el.offsetLeft;
              el.scrollLeft = scrollLeft - (x - startX);
            };
            const onUp = () => {
              el.style.cursor = "grab";
              el.style.userSelect = "";
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
        >
          {detailData.map((data, index) => {

            // file_pathが相対パスの場合、完全なURLに変換
            let fullFilePath = data.file_path;
            if (fullFilePath && !fullFilePath.startsWith('http')) {
              // S3のベースURLを追加
              fullFilePath = `${baseUrl}/${fullFilePath}`;
            }

            // thumbnailも同様に処理
            let fullThumbnail = data.thumbnail;
            if (fullThumbnail && !fullThumbnail.startsWith('http')) {
              fullThumbnail = `${baseUrl}/${fullThumbnail}`;
            }

            // 変換後のデータオブジェクト
            const processedData = {
              ...data,
              file_path: fullFilePath,
              thumbnail: fullThumbnail || fullFilePath, // thumbnailが空の場合はfile_pathを使用
            };

            const fileType = (data.file_type || "").toUpperCase();

            // YouTube動画とShortsを最優先でチェック
            // YouTube IDは通常11文字の英数字とハイフン、アンダースコアで構成される
            const isYouTubeId = /^[a-zA-Z0-9_-]{11}$/.test(data.file_name);
            const isShorts = fileType === "SHORTS" || fileType === "ショーツ" || fileType === "ショーツ動画" || fileType === "YOUTUBE SHORTS";

            if (isShorts) {
              return <YoutubeShorts key={index} {...processedData} fullWidth={isSolo} />;
            }
            else if (fileType === "YOUTUBE" || fileType === "YOUTUBE動画" || fileType === "YOUTUBE動画" || isYouTubeId) {
              return <Youtube key={index} {...processedData} fullWidth={isSolo} />;
            }
            // dataset タイプの場合、file_pathをカンマで分割して横スクロール表示
            else if (fileType === "DATASET" || fileType === "IMAGE_SET" || fileType === "IMAGESET") {
              return <ImageDataset key={index} {...processedData} />;
            }
            else if (fileType === "CTA") {
              return <Cta key={index} file_path={data.file_path} file_name={data.file_name} text={data.text} asset_title={data.asset_title} />;
            }
            // 画像ファイルを最優先でチェック (PNG, JPG, JPEG, GIF, etc.)
            else if (fileType === "IMG" || fileType === "PNG" || fileType === "JPG" ||
              fileType === "JPEG" || fileType === "GIF" || fileType === "WEBP" || fileType === "BMP") {
              return <Image key={index} {...processedData} />;
            }
            else if (fileType === "PDF") {
              return (
                <PDFViewer
                  key={index}
                  href={processedData.file_path}
                  imageSrc={processedData.thumbnail || ''}
                  imageAlt={processedData.file_name}
                  title={processedData.asset_title}
                  description={processedData.text}
                />
              );
            }
            // ショート動画 (MP4, MOV, etc.)
            else if (fileType === "VIDEO" || fileType === "ショート動画" ||
              fileType === "MP4" || fileType === "MOV" || fileType === "WEBM" || fileType === "AVI") {
              return <Video key={index} {...processedData} />;
            }
            // ナレッジRAGデータ (リンクカード)
            else if (fileType === "ナレッジRAGデータ" || fileType.includes("RAG") || fileType.includes("LINK")) {
              return <LinkCard key={index} {...processedData} />;
            }
            // その他のファイル (DOC, DOCX, XLS, XLSX, PPT, PPTX, ZIP, RAR, TXT, etc.)
            else if (fileType === "FILE" || fileType === "DOC" || fileType === "DOCX" ||
              fileType === "XLS" || fileType === "XLSX" || fileType === "PPT" || fileType === "PPTX" ||
              fileType === "ZIP" || fileType === "RAR" || fileType === "TXT" || fileType === "CSV" ||
              fileType === "ファイル" || fileType.includes("DOCUMENT")) {
              return <File key={index} {...processedData} />;
            }
            // デフォルトは画像として表示（拡張子がある場合）
            else {
              console.warn(`[DetailData] 不明なfile_type: ${data.file_type}`);
              // file_pathに画像拡張子が含まれているかチェック
              const path = (data.file_path || "").toLowerCase();
              if (path.includes('.png') || path.includes('.jpg') || path.includes('.jpeg') ||
                path.includes('.gif') || path.includes('.webp')) {
                return <Image key={index} {...processedData} />;
              }
              // file_pathに文書ファイル拡張子が含まれているかチェック
              if (path.includes('.doc') || path.includes('.xls') || path.includes('.ppt') ||
                path.includes('.zip') || path.includes('.rar') || path.includes('.txt') || path.includes('.csv')) {
                return <File key={index} {...processedData} />;
              }
              return <LinkCard key={index} {...processedData} />;
            }
          })}
        </div>
      )}
    </>
  );
}
